import axios from 'axios'
import { auth, db } from 'src/pages/api/server'
import {
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  onAuthStateChanged,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  signInWithEmailAndPassword
} from 'firebase/auth'
import type { User } from 'firebase/auth'
import { doc, updateDoc } from 'firebase/firestore'
import {
  State,
  ShoesState,
  DocumentApiResponse,
  ApiClientError,
  DocumentWithState,
  DocumentWithMetadata,
  PaymentListResponse,
  PaymentItemDetail,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  SetCustomClaimsRequest,
  SetCustomClaimsResponse,
  CustomClaims,
  FetchRetailResponse,
  RetailInfo,
  StateObj,
  SignatureResponse,
  PDFGenerationResponse,
  SaveSimulationRequest,
  SaveSimulationResponse,
  SendMailRequest,
  SendMailResponse,
  SaveShoesSimulationRequest,
  SaveShoesSimulationResponse,
  SendShoesMailRequest,
  SaveBatSimulationRequest,
  SaveBatSimulationResponse,
  SendBatMailRequest,
  UnifiedSendMailRequest,
  UpdatePaymentStatusRequest,
  UpdatePaymentStatusResponse,
  SendShoesShippingMailRequest,
  SendShoesShippingMailResponse,
  SendRetailOrderMailRequest,
  SendShoesOrderMailRequest,
  SendOrderMailResponse,
  SendShoesOrderFromPaymentRequest,
  SendShoesPaymentNotificationRequest,
  SendShoesPaymentNotificationResponse,
  FetchAnalysisDataRequest,
  FetchAnalysisDataResponse,
  PasswordResetResponse,
  SignInRequest,
  SignInResponse,
  Product
} from 'src/types'
import { PDFRequestData } from './pdf-auth'

/**
 * グローブのドキュメントを取得
 * @param documentId ドキュメントID
 * @returns グローブのレスポンスデータ（createdAtを含む）
 */
export const fetchGloveDocument = async (documentId: string): Promise<DocumentWithState<State>> => {
  try {
    const response = await axios.get<DocumentApiResponse>('/api/simulation/glove/fetch', {
      params: { documentId }
    })
    return {
      state: response.data.response.state as State,
      createdAt: response.data.response.createdAt
    }
  } catch (error) {
    console.error('グローブドキュメント取得エラー:', error)
    throw new ApiClientError('グローブドキュメントの取得に失敗しました', error)
  }
}

/**
 * シューズのドキュメントを取得
 * @param documentId ドキュメントID
 * @param includeMetadata メタデータ（savedId）を含めるかどうか（デフォルト: false）
 * @returns シューズの状態データとcreatedAt、メタデータを要求した場合はsavedIdも含む
 */
export const fetchShoesDocument = async (
  documentId: string,
  includeMetadata: boolean = false
): Promise<DocumentWithState<ShoesState> | DocumentWithMetadata<ShoesState>> => {
  try {
    if (!documentId || documentId.length < 1) {
      throw new ApiClientError('ドキュメントIDが指定されていません')
    }

    const response = await axios.get<DocumentApiResponse>('/api/simulation/shoes/fetch', {
      params: { documentId }
    })

    if (!response.data.response) {
      throw new ApiClientError('ドキュメントが見つかりませんでした')
    }

    const baseResult = {
      state: response.data.response.state as ShoesState,
      createdAt: response.data.response.createdAt,
      paymentIntentId: response.data.response?.paymentIntentId,
      purchaser: response.data.response?.purchaser,
      orderRequestedAt: response.data.response?.orderRequestedAt,
      orderAcceptedAt: response.data.response?.orderAcceptedAt,
      paymentCompletedAt: response.data.response?.paymentCompletedAt
    }

    if (includeMetadata) {
      return {
        ...baseResult,
        savedId: documentId
      } as DocumentWithMetadata<ShoesState>
    }

    return baseResult as DocumentWithState<ShoesState>
  } catch (error) {
    console.error('シューズドキュメント取得エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('シューズドキュメントの取得に失敗しました', error)
  }
}

/**
 * バットのドキュメントを取得
 * @param documentId ドキュメントID
 * @param includeMetadata メタデータ（savedId）を含めるかどうか（デフォルト: false）
 * @returns バットの状態データとcreatedAt、メタデータを要求した場合はsavedIdも含む
 */
export const fetchBatDocument = async (
  documentId: string,
  includeMetadata: boolean = false
): Promise<
  DocumentWithState<import('src/features/atoms/bat/types').BatState> | DocumentWithMetadata<import('src/features/atoms/bat/types').BatState>
> => {
  try {
    if (!documentId || documentId.length < 1) {
      throw new ApiClientError('ドキュメントIDが指定されていません')
    }

    const response = await axios.get<DocumentApiResponse>('/api/simulation/bat/fetch', {
      params: { documentId }
    })

    if (!response.data.response) {
      throw new ApiClientError('ドキュメントが見つかりませんでした')
    }

    const baseResult = {
      state: response.data.response.state as import('src/features/atoms/bat/types').BatState,
      createdAt: response.data.response.createdAt,
      paymentIntentId: response.data.response?.paymentIntentId,
      purchaser: response.data.response?.purchaser,
      orderRequestedAt: response.data.response?.orderRequestedAt,
      orderAcceptedAt: response.data.response?.orderAcceptedAt,
      paymentCompletedAt: response.data.response?.paymentCompletedAt
    }

    if (includeMetadata) {
      return {
        ...baseResult,
        savedId: documentId
      } as DocumentWithMetadata<import('src/features/atoms/bat/types').BatState>
    }

    return baseResult as DocumentWithState<import('src/features/atoms/bat/types').BatState>
  } catch (error) {
    console.error('バットドキュメント取得エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('バットドキュメントの取得に失敗しました', error)
  }
}

/**
 * 商品タイプに応じてドキュメントを取得
 * @param productType 商品タイプ（'glove' または 'shoes'）
 * @param documentId ドキュメントID
 * @returns 商品タイプに応じたレスポンスデータ（createdAtを含む）
 */
export const fetchDocument = async (
  productType: Product,
  documentId: string
): Promise<DocumentWithState<State | ShoesState | import('src/features/atoms/bat/types').BatState>> => {
  if (productType === 'shoes') {
    return await fetchShoesDocument(documentId)
  }

  if (productType === 'bat') {
    return await fetchBatDocument(documentId)
  }

  if (productType === 'glove') {
    return await fetchGloveDocument(documentId)
  }
}

/**
 * 支払い一覧を取得
 * @returns 支払いデータのリスト
 */
export const fetchPaymentList = async (): Promise<PaymentListResponse> => {
  try {
    const response = await axios.get<PaymentListResponse>('/api/payments/list')
    return response.data
  } catch (error) {
    console.error('支払いデータ取得エラー:', error)
    throw new ApiClientError('支払いデータの取得に失敗しました', error)
  }
}

/**
 * 支払いアイテムの詳細を取得
 * @param paymentIntentId PaymentIntentのID
 * @returns 支払いアイテムの詳細データ
 */
export const fetchPaymentItem = async (paymentIntentId: string): Promise<PaymentItemDetail> => {
  try {
    if (!paymentIntentId || paymentIntentId.length === 0) {
      throw new ApiClientError('PaymentIntentIDが指定されていません')
    }

    const response = await axios.get<PaymentItemDetail>(`/api/payments/item?payment_intent=${paymentIntentId}`)
    return response.data
  } catch (error) {
    console.error('支払いアイテム取得エラー:', error)
    throw new ApiClientError('支払いアイテムの取得に失敗しました', error)
  }
}

/**
 * 認証ヘッダー付きで支払いアイテムの詳細を取得
 * @param paymentIntentId PaymentIntentのID
 * @param idToken 認証トークン
 * @returns 支払いアイテムの詳細データ
 */
export const fetchPaymentItemWithAuth = async (paymentIntentId: string, idToken: string): Promise<PaymentItemDetail> => {
  try {
    if (!paymentIntentId || paymentIntentId.length === 0) {
      throw new ApiClientError('PaymentIntentIDが指定されていません')
    }

    if (!idToken || idToken.length === 0) {
      throw new ApiClientError('認証トークンが指定されていません')
    }

    const response = await axios.get<PaymentItemDetail>(`/api/payments/item?payment_intent=${paymentIntentId}`, {
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    })
    return response.data
  } catch (error) {
    console.error('認証付き支払いアイテム取得エラー:', error)
    throw new ApiClientError('認証付き支払いアイテムの取得に失敗しました', error)
  }
}

/**
 * チェックアウトセッションを作成
 * @param amount 金額
 * @param simulationId シミュレーションID
 * @returns チェックアウトセッションのレスポンスデータ
 */
export const createCheckoutSession = async (amount: number, simulationId: string): Promise<CreateCheckoutSessionResponse> => {
  try {
    if (!amount || amount <= 0) {
      throw new ApiClientError('有効な金額が指定されていません')
    }

    if (!simulationId || simulationId.length === 0) {
      throw new ApiClientError('シミュレーションIDが指定されていません')
    }

    const requestData: CreateCheckoutSessionRequest = {
      amount,
      simulationId
    }

    const response = await axios.post<CreateCheckoutSessionResponse>('/api/payments/sessions', requestData)
    return response.data
  } catch (error) {
    console.error('チェックアウトセッション作成エラー:', error)
    throw new ApiClientError('チェックアウトセッションの作成に失敗しました', error)
  }
}

/**
 * カスタムクレームを設定
 * @param email ユーザーのメールアドレス
 * @param customClaims 設定するカスタムクレーム
 * @returns 設定結果のレスポンスデータ
 */
export const setCustomClaims = async (email: string, customClaims: CustomClaims): Promise<SetCustomClaimsResponse> => {
  try {
    if (!email || email.length === 0) {
      throw new ApiClientError('メールアドレスが指定されていません')
    }

    if (!customClaims || Object.keys(customClaims).length === 0) {
      throw new ApiClientError('カスタムクレームが指定されていません')
    }

    const requestData: SetCustomClaimsRequest = {
      email,
      customClaims
    }

    const response = await axios.post<SetCustomClaimsResponse>('/api/auth/custom-claims', requestData)
    return response.data
  } catch (error) {
    console.error('カスタムクレーム設定エラー:', error)
    throw new ApiClientError('カスタムクレームの設定に失敗しました', error)
  }
}

/**
 * 小売店情報を取得（名前と選択可能ブランド）
 * @param email 小売店のメールアドレス
 * @returns 小売店情報（名前、メールアドレス、選択可能ブランド）
 */
export const fetchRetail = async (email: string): Promise<RetailInfo & { selectableBrands: StateObj[] }> => {
  try {
    if (!email || email.length === 0) {
      throw new ApiClientError('メールアドレスが指定されていません')
    }

    const { data } = await axios.get<FetchRetailResponse>(`/api/retail?email=${encodeURIComponent(email)}`)
    return {
      name: data.retail.name,
      email: data.retail.email,
      selectableBrands: data.retail.selectableBrands,
      createdAt: {
        nanoseconds: 0,
        secondes: Date.now() / 1000
      }
    }
  } catch (error) {
    console.error('小売店情報取得エラー:', error)
    throw new ApiClientError('小売店情報の取得に失敗しました', error)
  }
}

/**
 * クライアント側からPDF生成サービスを直接呼び出す
 * @param requestData PDF生成リクエストデータ
 * @param idToken Firebase IDトークン
 * @returns PDF Blob
 */
export const callPDFServiceFromClient = async (requestData: PDFRequestData, idToken: string): Promise<PDFGenerationResponse> => {
  try {
    if (!idToken || idToken.length === 0) {
      throw new ApiClientError('IDトークンが指定されていません')
    }

    // Step 1: 署名生成APIを呼び出して認証情報を取得
    console.log('署名生成API呼び出し中...', {
      product: requestData.product,
      savedId: requestData.savedId
    })

    const signatureResponse = await axios.post<SignatureResponse>(
      '/api/pdf/signature',
      { requestData },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    )

    const { signature, timestamp, clientId, serviceUrl } = signatureResponse.data

    console.log('署名取得完了、PDF生成サービス呼び出し中...', {
      serviceUrl,
      timestamp,
      clientId
    })

    // Step 2: PDF生成サービスを直接呼び出し
    const pdfResponse = await axios.post(`${serviceUrl}/api/pdf/create-document`, requestData, {
      headers: {
        'Content-Type': 'application/json',
        'X-Signature': signature,
        'X-Timestamp': timestamp,
        'X-Client-ID': clientId
      },
      responseType: 'blob'
    })

    console.log('PDF生成完了')

    // Step 3: レスポンスをBlobとして返却
    const pdfBlob = pdfResponse.data

    if (pdfBlob.size === 0) {
      throw new ApiClientError('生成されたPDFが空です')
    }

    return pdfBlob
  } catch (error) {
    console.error('PDF生成クライアント呼び出しエラー:', error)

    if (error instanceof ApiClientError) {
      throw error
    }

    if (error instanceof Error) {
      throw new ApiClientError(`PDF生成処理が失敗しました: ${error.message}`, error)
    }

    throw new ApiClientError(`PDF生成処理が失敗しました: ${String(error)}`, error)
  }
}

/**
 * PDFをダウンロードするヘルパー関数
 * @param pdfBlob PDF Blob
 * @param filename ダウンロードファイル名
 */
export const downloadPDF = (pdfBlob: Blob, filename: string): void => {
  try {
    if (!pdfBlob || pdfBlob.size === 0) {
      throw new ApiClientError('有効なPDF Blobが指定されていません')
    }

    if (!filename || filename.length === 0) {
      throw new ApiClientError('ファイル名が指定されていません')
    }

    const url = window.URL.createObjectURL(pdfBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  } catch (error) {
    console.error('PDF ダウンロードエラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('PDFのダウンロードに失敗しました', error)
  }
}

/**
 * order-acceptance専用のPDF生成関数（Firebase Auth不要）
 * @param simulationId シミュレーションID
 * @param orderRequestedAt 発注リクエスト時刻
 * @param requestData PDF生成リクエストデータ
 * @returns PDF Blob
 */
export const generateOrderAcceptancePDF = async (orderRequestedAt: string, requestData: PDFRequestData): Promise<PDFGenerationResponse> => {
  try {
    if (!requestData.savedId || requestData.savedId.length === 0) {
      throw new ApiClientError('シミュレーションIDが指定されていません')
    }

    if (!orderRequestedAt || orderRequestedAt.length === 0) {
      throw new ApiClientError('orderRequestedAtが指定されていません')
    }

    if (!requestData) {
      throw new ApiClientError('PDF生成リクエストデータが指定されていません')
    }

    console.log('order-acceptance 署名取得API呼び出し中...', {
      orderRequestedAt,
      product: requestData.product,
      savedId: requestData.savedId
    })

    // Step 1: 署名生成APIを呼び出して認証情報を取得
    const signatureResponse = await axios.post<{
      signature: string
      timestamp: string
      clientId: string
      serviceUrl: string
    }>(
      '/api/pdf/order-acceptance-signature',
      {
        orderRequestedAt,
        requestData
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )

    const { signature, timestamp, clientId, serviceUrl } = signatureResponse.data

    console.log('署名取得完了、外部PDF生成サービス呼び出し中...', {
      serviceUrl,
      timestamp,
      clientId
    })

    // Step 2: 外部PDF生成サービスを直接呼び出し
    const pdfResponse = await axios.post(`${serviceUrl}/api/pdf/create-document`, requestData, {
      headers: {
        'Content-Type': 'application/json',
        'X-Signature': signature,
        'X-Timestamp': timestamp,
        'X-Client-ID': clientId
      },
      responseType: 'blob'
    })

    // レスポンスをBlobとして返却
    const pdfBlob = pdfResponse.data

    if (pdfBlob.size === 0) {
      throw new ApiClientError('生成されたPDFが空です')
    }

    return pdfBlob
  } catch (error) {
    console.error('order-acceptance PDF生成エラー:', error)

    if (axios.isAxiosError(error)) {
      if (error.response?.data) {
        // エラーレスポンスがJSONの場合
        try {
          const errorData = typeof error.response.data === 'string' ? JSON.parse(error.response.data) : error.response.data
          throw new ApiClientError(errorData.message || 'PDF生成に失敗しました', error)
        } catch (parseError) {
          throw new ApiClientError(`PDF生成に失敗しました: ${error.message}`, error)
        }
      }
      throw new ApiClientError(`PDF生成に失敗しました: ${error.message}`, error)
    }

    if (error instanceof ApiClientError) {
      throw error
    }

    if (error instanceof Error) {
      throw new ApiClientError(`PDF生成処理が失敗しました: ${error.message}`, error)
    }

    throw new ApiClientError(`PDF生成処理が失敗しました: ${String(error)}`, error)
  }
}

/**
 * PDF BlobをBase64文字列に変換するヘルパー関数
 * @param pdfBlob PDF Blob
 * @returns Base64文字列のPromise
 */
export const pdfBlobToBase64 = async (pdfBlob: Blob): Promise<string> => {
  try {
    if (!pdfBlob || pdfBlob.size === 0) {
      throw new ApiClientError('有効なPDF Blobが指定されていません')
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // data:application/pdf;base64, プレフィックスを除去
          const base64 = reader.result.split(',')[1]
          resolve(base64)
        } else {
          reject(new ApiClientError('FileReader result is not a string'))
        }
      }
      reader.onerror = () => reject(new ApiClientError('PDF Blob読み込みエラー'))
      reader.readAsDataURL(pdfBlob)
    })
  } catch (error) {
    console.error('PDF Base64変換エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('PDF Base64変換に失敗しました', error)
  }
}

/**
 * シミュレーションを保存
 * @param state 保存するシミュレーション状態
 * @returns 保存されたシミュレーションのID
 */
export const saveSimulation = async (state: SaveSimulationRequest['state']): Promise<SaveSimulationResponse> => {
  try {
    if (!state) {
      throw new ApiClientError('保存するシミュレーション状態が指定されていません')
    }

    const requestData: SaveSimulationRequest = { state }
    const response = await axios.post<SaveSimulationResponse>('/api/simulation/glove/create', requestData)

    if (!response.data.id) {
      throw new ApiClientError('シミュレーションの保存に失敗しました - IDが返されませんでした')
    }

    return response.data
  } catch (error) {
    console.error('シミュレーション保存エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('シミュレーションの保存に失敗しました', error)
  }
}

/**
 * メールを送信（レガシー関数、新規開発ではsendUnifiedMailを使用）
 * @param mailData 送信するメールのデータ
 * @returns 送信結果
 */
export const sendMail = async (mailData: SendMailRequest): Promise<SendMailResponse> => {
  try {
    if (!mailData.subject || mailData.subject.length === 0) {
      throw new ApiClientError('メールの件名が指定されていません')
    }

    if (!mailData.savedId || mailData.savedId.length === 0) {
      throw new ApiClientError('保存IDが指定されていません')
    }

    if (!mailData.email || mailData.email.length === 0) {
      throw new ApiClientError('送信先メールアドレスが指定されていません')
    }

    if (!mailData.imageUrls || !mailData.imageUrls.front || !mailData.imageUrls.rear) {
      throw new ApiClientError('画像URLが正しく指定されていません')
    }

    if (!mailData.locale || mailData.locale.length === 0) {
      throw new ApiClientError('ロケールが指定されていません')
    }

    const response = await axios.post<SendMailResponse>('/api/mail/simulation', mailData, {
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return response.data
  } catch (error) {
    console.error('メール送信エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('メールの送信に失敗しました', error)
  }
}

/**
 * シューズのシミュレーションを保存
 * @param state 保存するシューズシミュレーション状態
 * @returns 保存されたシミュレーションのID
 */
export const saveShoesSimulation = async (state: SaveShoesSimulationRequest['state']): Promise<SaveShoesSimulationResponse> => {
  try {
    if (!state) {
      throw new ApiClientError('保存するシューズシミュレーション状態が指定されていません')
    }

    const requestData: SaveShoesSimulationRequest = { state }
    const response = await axios.post<SaveShoesSimulationResponse>('/api/simulation/shoes/create', requestData)

    if (!response.data.id) {
      throw new ApiClientError('シューズシミュレーションの保存に失敗しました - IDが返されませんでした')
    }

    return response.data
  } catch (error) {
    console.error('シューズシミュレーション保存エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('シューズシミュレーションの保存に失敗しました', error)
  }
}

/**
 * シューズ用メールを送信（レガシー関数、新規開発ではsendUnifiedMailを使用）
 * @param mailData 送信するメールのデータ
 * @returns 送信結果
 */
export const sendShoesMail = async (mailData: SendShoesMailRequest): Promise<SendMailResponse> => {
  try {
    if (!mailData.subject || mailData.subject.length === 0) {
      throw new ApiClientError('メールの件名が指定されていません')
    }

    if (!mailData.savedId || mailData.savedId.length === 0) {
      throw new ApiClientError('保存IDが指定されていません')
    }

    if (!mailData.email || mailData.email.length === 0) {
      throw new ApiClientError('送信先メールアドレスが指定されていません')
    }

    if (!mailData.imageUrls || !mailData.imageUrls.front || !mailData.imageUrls.side || !mailData.imageUrls.rear) {
      throw new ApiClientError('画像URLが正しく指定されていません（front, side, rear が必要）')
    }

    if (!mailData.locale || mailData.locale.length === 0) {
      throw new ApiClientError('ロケールが指定されていません')
    }

    const response = await axios.post<SendMailResponse>('/api/mail/simulation', mailData, {
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return response.data
  } catch (error) {
    console.error('シューズ用メール送信エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('シューズ用メールの送信に失敗しました', error)
  }
}

/**
 * 統合されたメール送信（グローブ・シューズ両方対応）
 * @param mailData 送信するメールのデータ
 * @returns 送信結果
 */
export const sendUnifiedMail = async (mailData: UnifiedSendMailRequest): Promise<SendMailResponse> => {
  try {
    if (!mailData.subject || mailData.subject.length === 0) {
      throw new ApiClientError('メールの件名が指定されていません')
    }

    if (!mailData.savedId || mailData.savedId.length === 0) {
      throw new ApiClientError('保存IDが指定されていません')
    }

    if (!mailData.email || mailData.email.length === 0) {
      throw new ApiClientError('送信先メールアドレスが指定されていません')
    }

    if (!mailData.imageUrls || !mailData.imageUrls.front || !mailData.imageUrls.rear) {
      throw new ApiClientError('画像URLが正しく指定されていません（front, rear は必須）')
    }

    if (!mailData.locale || mailData.locale.length === 0) {
      throw new ApiClientError('ロケールが指定されていません')
    }

    const response = await axios.post<SendMailResponse>('/api/mail/simulation', mailData, {
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return response.data
  } catch (error) {
    console.error('メール送信エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('メールの送信に失敗しました', error)
  }
}

/**
 * 支払いステータスを更新
 * @param requestData 支払いステータス更新リクエストデータ
 * @returns 更新結果
 */
export const updatePaymentStatus = async (requestData: Omit<UpdatePaymentStatusRequest, 'createdAt'>): Promise<UpdatePaymentStatusResponse> => {
  try {
    if (!requestData.paymentId || requestData.paymentId.length === 0) {
      throw new ApiClientError('PaymentIDが指定されていません')
    }

    if (!requestData.newStatus || requestData.newStatus.length === 0) {
      throw new ApiClientError('新しいステータスが指定されていません')
    }

    const requestDataWithTimestamp: UpdatePaymentStatusRequest = {
      ...requestData,
      createdAt: {
        nanoseconds: 0,
        secondes: Date.now() / 1000
      }
    }

    const response = await axios.post<UpdatePaymentStatusResponse>('/api/payments/status', requestDataWithTimestamp)

    return {
      success: response.data.success,
      createdAt: {
        nanoseconds: 0,
        secondes: Date.now() / 1000
      }
    }
  } catch (error) {
    console.error('支払いステータス更新エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('支払いステータスの更新に失敗しました', error)
  }
}

/**
 * 認証ヘッダー付きで支払いステータスを更新
 * @param requestData 支払いステータス更新リクエストデータ
 * @param idToken 認証トークン
 * @returns 更新結果
 */
export const updatePaymentStatusWithAuth = async (
  requestData: Omit<UpdatePaymentStatusRequest, 'createdAt'>,
  idToken: string
): Promise<UpdatePaymentStatusResponse> => {
  try {
    if (!requestData.paymentId || requestData.paymentId.length === 0) {
      throw new ApiClientError('PaymentIDが指定されていません')
    }

    if (!requestData.newStatus || requestData.newStatus.length === 0) {
      throw new ApiClientError('新しいステータスが指定されていません')
    }

    if (!idToken || idToken.length === 0) {
      throw new ApiClientError('認証トークンが指定されていません')
    }

    const requestDataWithTimestamp: UpdatePaymentStatusRequest = {
      ...requestData,
      createdAt: {
        nanoseconds: 0,
        secondes: Date.now() / 1000
      }
    }

    const response = await axios.post<UpdatePaymentStatusResponse>('/api/payments/status', requestDataWithTimestamp, {
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    })

    return {
      success: response.data.success,
      createdAt: {
        nanoseconds: 0,
        secondes: Date.now() / 1000
      }
    }
  } catch (error) {
    console.error('認証付き支払いステータス更新エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('認証付き支払いステータスの更新に失敗しました', error)
  }
}

/**
 * 認証ヘッダー付きでシューズ発送完了メールを送信
 * @param requestData 発送完了メール送信リクエストデータ
 * @param idToken 認証トークン
 * @returns 送信結果
 */
export const sendShoesShippingMailWithAuth = async (
  requestData: SendShoesShippingMailRequest,
  idToken: string
): Promise<SendShoesShippingMailResponse> => {
  try {
    if (!requestData.paymentData) {
      throw new ApiClientError('支払いデータが指定されていません')
    }

    if (!requestData.trackingNumber || requestData.trackingNumber.length === 0) {
      throw new ApiClientError('追跡番号が指定されていません')
    }

    if (!requestData.shippingCompany || requestData.shippingCompany.length === 0) {
      throw new ApiClientError('運送会社が指定されていません')
    }

    if (!idToken || idToken.length === 0) {
      throw new ApiClientError('認証トークンが指定されていません')
    }

    const response = await axios.post<SendShoesShippingMailResponse>('/api/mail/shoes/shipping', requestData, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
    })

    return response.data
  } catch (error) {
    console.error('シューズ発送完了メール送信エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('シューズ発送完了メールの送信に失敗しました', error)
  }
}

/**
 * 小売店発注メールを送信
 * @param requestData 小売店発注メール送信リクエストデータ
 * @returns 送信結果
 */
export const sendRetailOrderMail = async (requestData: SendRetailOrderMailRequest): Promise<SendOrderMailResponse> => {
  try {
    if (!requestData.subject || requestData.subject.length === 0) {
      throw new ApiClientError('メールの件名が指定されていません')
    }

    if (!requestData.agencyEmail || requestData.agencyEmail.length === 0) {
      throw new ApiClientError('代理店メールアドレスが指定されていません')
    }

    if (!requestData.retailEmail || requestData.retailEmail.length === 0) {
      throw new ApiClientError('小売店メールアドレスが指定されていません')
    }

    if (!requestData.savedId || requestData.savedId.length === 0) {
      throw new ApiClientError('保存IDが指定されていません')
    }

    if (!requestData.pdfBase64 || requestData.pdfBase64.length === 0) {
      throw new ApiClientError('PDF Base64データが指定されていません')
    }

    const response = await axios.post<SendOrderMailResponse>(
      '/api/mail/order',
      { body: requestData },
      {
        headers: {
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json'
        }
      }
    )

    return {
      success: response.status === 200
    }
  } catch (error) {
    console.error('小売店発注メール送信エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('小売店発注メールの送信に失敗しました', error)
  }
}

/**
 * 認証ヘッダー付きで支払い管理からシューズ発注メールを送信
 * @param requestData 支払い管理からのシューズ発注メール送信リクエストデータ
 * @param idToken 認証トークン
 * @returns 送信結果
 */
export const sendShoesOrderFromPaymentWithAuth = async (
  requestData: SendShoesOrderFromPaymentRequest,
  idToken: string
): Promise<SendOrderMailResponse> => {
  try {
    if (!requestData.simulationId || requestData.simulationId.length === 0) {
      throw new ApiClientError('シミュレーションIDが指定されていません')
    }

    // if (!requestData.pdfBase64 || requestData.pdfBase64.length === 0) {
    //   throw new ApiClientError('PDF Base64データが指定されていません')
    // }

    if (!requestData.orderRequestedAt || requestData.orderRequestedAt.length === 0) {
      throw new ApiClientError('発注依頼日時が指定されていません')
    }

    if (!idToken || idToken.length === 0) {
      throw new ApiClientError('認証トークンが指定されていません')
    }

    const response = await axios.post<SendOrderMailResponse>('/api/mail/shoes/order', requestData, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json'
      }
    })

    return {
      success: response.status === 200
    }
  } catch (error) {
    console.error('支払い管理からのシューズ発注メール送信エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('支払い管理からのシューズ発注メールの送信に失敗しました', error)
  }
}

/**
 * 分析用のドキュメントを取得
 * @param requestData 分析データ取得リクエストデータ
 * @returns 検索条件に一致するドキュメントのリスト
 */
export const fetchDocumentForAnalysis = async (requestData: FetchAnalysisDataRequest): Promise<FetchAnalysisDataResponse> => {
  try {
    if (!requestData.league && !requestData.position && !requestData.brand) {
      throw new ApiClientError('検索条件が指定されていません（league, position, brand のいずれかは必須）')
    }

    const response = await axios.get<FetchAnalysisDataResponse>('/api/analysis', {
      params: requestData
    })

    if (!response.data.response) {
      throw new ApiClientError('分析データが見つかりませんでした')
    }

    return response.data
  } catch (error) {
    console.error('分析データ取得エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('分析データの取得に失敗しました', error)
  }
}

/**
 * オーナー認証の初期化を行う
 * Firebase認証の永続化設定とオーナーメールの検証を実行
 * @param ownerEmail オーナーのメールアドレス（環境変数から取得）
 * @param onUnauthorized 認証に失敗した場合のコールバック
 * @returns Promise<void>
 */
export const initializeOwnerAuthentication = async (ownerEmail: string | undefined, onUnauthorized: () => void): Promise<void> => {
  try {
    // 認証状態の永続化を設定
    await setPersistence(auth, browserLocalPersistence)

    // 認証状態の変更を監視
    onAuthStateChanged(auth, async (user: User | null) => {
      if (user?.email !== ownerEmail) {
        onUnauthorized()
      }
    })
  } catch (error) {
    console.error('認証状態の永続化に失敗しました:', error)
    throw new ApiClientError('認証初期化に失敗しました', error)
  }
}

/**
 * 小売店向け認証の初期化を行う
 * Firebase認証の永続化設定とユーザー情報の取得を実行
 * @param onUserAuthenticated ユーザーが認証された場合のコールバック（メールアドレスを受け取る）
 * @returns Promise<void>
 */
export const initializeRetailAuthentication = async (onUserAuthenticated: (email: string | null) => void): Promise<void> => {
  try {
    // 認証状態の永続化を設定
    await setPersistence(auth, browserLocalPersistence)

    // 認証状態の変更を監視
    onAuthStateChanged(auth, async (user: User | null) => {
      if (user) {
        onUserAuthenticated(user.email)
      } else {
        onUserAuthenticated(null)
      }
    })
  } catch (error) {
    console.error('認証状態の永続化に失敗しました:', error)
    throw new ApiClientError('小売店認証初期化に失敗しました', error)
  }
}

/**
 * ユーザーをサインアウトしてリダイレクトする
 * @param redirectTo サインアウト後のリダイレクト先
 * @returns Promise<void>
 */
export const signOutAndRedirect = async (redirectTo: () => void): Promise<void> => {
  try {
    await auth.signOut()
    redirectTo()
  } catch (error) {
    console.error('ログアウトに失敗しました', error)
    throw new ApiClientError('サインアウトに失敗しました', error)
  }
}

/**
 * 統一認証初期化（小売店・支払い管理者両方に対応）
 * ユーザーの認証状態を監視し、権限レベルを返す
 * @param onAuthenticated 認証成功時のコールバック（ユーザー情報と権限情報を受け取る）
 * @param onUnauthorized 未認証時のコールバック
 * @returns Promise<void>
 */
export const initializeUnifiedAuthentication = async (
  onAuthenticated: (user: { email: string | null; isPaymentAdmin: boolean }) => void,
  onUnauthorized: () => void
): Promise<void> => {
  try {
    // 認証状態の永続化を設定
    await setPersistence(auth, browserLocalPersistence)

    // 認証状態の変更を監視
    onAuthStateChanged(auth, async (user: User | null) => {
      if (!user) {
        return onUnauthorized()
      }

      try {
        // IDトークンを取得してカスタムクレームをチェック
        const idTokenResult = await user.getIdTokenResult()
        const customClaims = idTokenResult.claims
        const isPaymentAdmin = !!customClaims?.isPaymentAdmin

        onAuthenticated({
          email: user.email,
          isPaymentAdmin
        })
      } catch (error) {
        console.error('カスタムクレーム取得エラー:', error)
        // カスタムクレーム取得に失敗した場合でも、一般ユーザーとして認証
        onAuthenticated({
          email: user.email,
          isPaymentAdmin: false
        })
      }
    })
  } catch (error) {
    console.error('認証状態の永続化に失敗しました:', error)
    throw new ApiClientError('統一認証初期化に失敗しました', error)
  }
}

/**
 * 一般ユーザー向け認証の初期化を行う
 * 未認証ユーザーをサインインページにリダイレクトし、認証済みユーザーの状態を監視
 * @param onAuthenticated 認証成功時のコールバック
 * @param onUnauthorized 未認証時のコールバック（リダイレクト処理を含む）
 * @returns Promise<void>
 */
export const initializeUserAuthentication = async (onAuthenticated: () => void, onUnauthorized: () => void): Promise<void> => {
  try {
    // 認証状態の変更を監視
    onAuthStateChanged(auth, (user: User | null) => {
      if (!user) {
        onUnauthorized()
        return
      }
      onAuthenticated()
    })
  } catch (error) {
    console.error('認証初期化に失敗しました:', error)
    throw new ApiClientError('一般ユーザー認証初期化に失敗しました', error)
  }
}

/**
 * 現在認証されているユーザーのIDトークンを取得
 * @param forceRefresh トークンを強制的に再取得するかどうか（デフォルト: false）
 * @returns Promise<string> IDトークン
 * @throws ApiClientError ユーザーが認証されていない場合またはトークン取得に失敗した場合
 */
export const getCurrentUserIdToken = async (forceRefresh: boolean = false): Promise<string> => {
  try {
    const user = auth.currentUser
    if (!user) {
      throw new ApiClientError('ユーザーが認証されていません')
    }

    const idToken = await user.getIdToken(forceRefresh)
    if (!idToken || idToken.length === 0) {
      throw new ApiClientError('IDトークンの取得に失敗しました')
    }

    return idToken
  } catch (error) {
    console.error('IDトークン取得エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('IDトークンの取得に失敗しました', error)
  }
}

/**
 * パスワードリセットメールを送信
 * Firebase Authのパスワードリセット機能をラップした関数
 * @param email リセット対象のメールアドレス
 * @returns 送信結果
 */
export const sendPasswordResetEmail = async (email: string): Promise<PasswordResetResponse> => {
  try {
    if (!email || email.length === 0) {
      throw new ApiClientError('メールアドレスが指定されていません')
    }

    // メールアドレスの基本的なバリデーション
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      throw new ApiClientError('有効なメールアドレスを入力してください')
    }

    // Firebase Authのパスワードリセット機能を直接呼び出し
    await firebaseSendPasswordResetEmail(auth, email)

    return {
      success: true,
      message: 'パスワードリセットメールを送信しました'
    }
  } catch (error) {
    console.error('パスワードリセットメール送信エラー:', error)

    // Firebase Authのエラーをキャッチして適切なメッセージを返す
    if (error instanceof Error) {
      if (error.message.includes('user-not-found')) {
        throw new ApiClientError('指定されたメールアドレスは登録されていません')
      } else if (error.message.includes('invalid-email')) {
        throw new ApiClientError('有効なメールアドレスを入力してください')
      } else if (error.message.includes('too-many-requests')) {
        throw new ApiClientError('リクエストが多すぎます。しばらく時間をおいてから再試行してください')
      }
    }

    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('パスワードリセットメールの送信に失敗しました', error)
  }
}

/**
 * APIクライアントのエラー型をエクスポート
 */
export { ApiClientError }

/**
 * ユーザーサインイン
 * Firebase Authのサインイン機能をラップした関数
 * @param requestData サインインリクエストデータ
 * @returns サインイン結果
 */
export const signInUser = async (requestData: SignInRequest): Promise<SignInResponse> => {
  try {
    if (!requestData.email || requestData.email.length === 0) {
      throw new ApiClientError('メールアドレスが指定されていません')
    }

    if (!requestData.password || requestData.password.length === 0) {
      throw new ApiClientError('パスワードが指定されていません')
    }

    // メールアドレスの基本的なバリデーション
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(requestData.email)) {
      throw new ApiClientError('有効なメールアドレスを入力してください')
    }

    // Firebase認証の永続化を設定（セッション期間中のみ）
    await setPersistence(auth, browserSessionPersistence)

    // Firebase Authのサインイン機能を呼び出し
    const userCredential = await signInWithEmailAndPassword(auth, requestData.email, requestData.password)

    return {
      success: true,
      message: 'サインインが完了しました',
      user: {
        uid: userCredential.user.uid,
        email: userCredential.user.email
      },
      createdAt: {
        nanoseconds: 0,
        secondes: Date.now() / 1000
      }
    }
  } catch (error) {
    console.error('サインインエラー:', error)

    // Firebase Authのエラーをキャッチして適切なメッセージを返す
    if (error instanceof Error) {
      if (error.message.includes('user-not-found')) {
        throw new ApiClientError('指定されたメールアドレスは登録されていません')
      } else if (error.message.includes('wrong-password')) {
        throw new ApiClientError('パスワードが正しくありません')
      } else if (error.message.includes('invalid-email')) {
        throw new ApiClientError('有効なメールアドレスを入力してください')
      } else if (error.message.includes('user-disabled')) {
        throw new ApiClientError('このアカウントは無効になっています')
      } else if (error.message.includes('too-many-requests')) {
        throw new ApiClientError('ログイン試行回数が多すぎます。しばらく時間をおいてから再試行してください')
      } else if (error.message.includes('invalid-credential')) {
        throw new ApiClientError('メールアドレスまたはパスワードが正しくありません')
      }
    }

    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('サインインに失敗しました', error)
  }
}

/**
 * Firestoreドキュメントを更新する
 * @param collection コレクション名
 * @param documentId ドキュメントID
 * @param data 更新するデータ
 */
export const updateFirestoreDocument = async (collection: string, documentId: string, data: Record<string, any>): Promise<void> => {
  try {
    const docRef = doc(db, collection, documentId)
    await updateDoc(docRef, data)
  } catch (error) {
    console.error('Firestoreドキュメントの更新に失敗しました:', error)
    throw new ApiClientError('ドキュメントの更新に失敗しました', error)
  }
}

/**
 * シューズ購入完了メールを送信
 * @param requestData 購入完了メール送信リクエストデータ
 * @returns 送信結果
 */
export const sendShoesPurchaseCompletedMail = async (requestData: {
  simulationId: string
  orderer: {
    name: string
    mailAddress: string
    phoneNumber: string
  }
  createdAt: string
}): Promise<{ success: boolean }> => {
  try {
    if (!requestData.simulationId || requestData.simulationId.length === 0) {
      throw new ApiClientError('シミュレーションIDが指定されていません')
    }

    if (!requestData.orderer.mailAddress || requestData.orderer.mailAddress.length === 0) {
      throw new ApiClientError('注文者のメールアドレスが指定されていません')
    }

    if (!requestData.createdAt || requestData.createdAt.length === 0) {
      throw new ApiClientError('注文依頼日時が指定されていません')
    }

    const response = await axios.post(`${process.env.NEXT_PUBLIC_ORIGIN}/api/mail/shoes/purchaseCompleted`, requestData, {
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return {
      success: response.status === 200
    }
  } catch (error) {
    console.error('シューズ購入完了メール送信エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('シューズ購入完了メールの送信に失敗しました', error)
  }
}

/**
 * ATOMS本社宛決済完了通知メールを送信
 * @param requestData 決済完了通知メール送信リクエストデータ
 * @returns 送信結果
 */
export const sendShoesPaymentNotificationMail = async (
  requestData: SendShoesPaymentNotificationRequest
): Promise<SendShoesPaymentNotificationResponse> => {
  try {
    if (!requestData.simulationId || requestData.simulationId.length === 0) {
      throw new ApiClientError('シミュレーションIDが指定されていません')
    }

    if (!requestData.createdAt || requestData.createdAt.length === 0) {
      throw new ApiClientError('決済完了日時が指定されていません')
    }

    const response = await axios.post(`${process.env.NEXT_PUBLIC_ORIGIN}/api/mail/shoes/paymentNotification`, requestData, {
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return {
      success: response.status === 200
    }
  } catch (error) {
    console.error('ATOMS本社宛決済完了通知メール送信エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('ATOMS本社宛決済完了通知メールの送信に失敗しました', error)
  }
}

/**
 * バットのシミュレーションを保存
 * @param state 保存するバットシミュレーション状態
 * @returns 保存されたシミュレーションのID
 */
export const saveBatSimulation = async (
  state: import('src/types').SaveBatSimulationRequest['state']
): Promise<import('src/types').SaveBatSimulationResponse> => {
  try {
    if (!state) {
      throw new ApiClientError('保存するバットシミュレーション状態が指定されていません')
    }

    const requestData: import('src/types').SaveBatSimulationRequest = { state }
    const response = await axios.post<import('src/types').SaveBatSimulationResponse>('/api/simulation/bat/create', requestData)

    if (!response.data.id) {
      throw new ApiClientError('バットシミュレーションの保存に失敗しました - IDが返されませんでした')
    }

    return response.data
  } catch (error) {
    console.error('バットシミュレーション保存エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('バットシミュレーションの保存に失敗しました', error)
  }
}

/**
 * バット用メールを送信
 * @param mailData 送信するメールのデータ
 * @returns 送信結果
 */
export const sendBatMail = async (mailData: import('src/types').SendBatMailRequest): Promise<SendMailResponse> => {
  try {
    if (!mailData.subject || mailData.subject.length === 0) {
      throw new ApiClientError('メールの件名が指定されていません')
    }

    if (!mailData.savedId || mailData.savedId.length === 0) {
      throw new ApiClientError('保存IDが指定されていません')
    }

    if (!mailData.email || mailData.email.length === 0) {
      throw new ApiClientError('送信先メールアドレスが指定されていません')
    }

    if (!mailData.imageUrls || !mailData.imageUrls.front) {
      throw new ApiClientError('画像URLが正しく指定されていません（front は必須）')
    }

    if (!mailData.locale || mailData.locale.length === 0) {
      throw new ApiClientError('ロケールが指定されていません')
    }

    const response = await axios.post<SendMailResponse>('/api/mail/simulation', mailData, {
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return response.data
  } catch (error) {
    console.error('バット用メール送信エラー:', error)
    if (error instanceof ApiClientError) {
      throw error
    }
    throw new ApiClientError('バット用メールの送信に失敗しました', error)
  }
}
